/**
 * Public Job Application — /apply
 *
 * Public-facing, indexable. Multi-step form. Save-as-you-go via tokenized
 * progress link. No SSN, no BG check at this stage.
 *
 * Heart of the page: the manifesto block at the top frames who New Leaf
 * is FOR before the form ever opens. The 3 narrative ("heart") questions
 * are the screening surface — the costly-service question is the keystone.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { MetaFunction } from 'react-router';
import { Link } from 'react-router';
import { formatPhoneInput } from '@shared/utils';
import type {
  ApplicantSubmitPayload,
  ApplicantProgressPayload,
  ApplicantReference,
  ApplicantExperienceType,
} from '@shared/types';
// No `SITE` import — this page speaks AS whichever company an applicant is
// applying to, and every value it needs now comes from the resolved branding.
import { useBranding, brandingFromMatches } from '~/lib/branding';
import '~/styles/apply.css';

/**
 * New Leaf Cleaning's own copy for this page — its market, its street address,
 * its registered legal name.
 *
 * EVERY literal in this file that names New Leaf, its city or its market lives
 * in this one object, and every read of it is behind `branding.isNewLeaf`. The
 * unit test in tests/unit/tenant-brand-leak.test.ts scans the rest of the file
 * and fails on any such literal outside these braces, so the boundary is
 * mechanical rather than a matter of remembering.
 *
 * It matters here more than on a marketing page: this is a hiring page every
 * tenant links to from its own job posts, its JobPosting structured data is
 * read by Google and shown directly to job-seekers, and its photo-release line
 * is a consent naming a legal entity. Telling an applicant to expect work in
 * St. Petersburg on behalf of a company that does not operate there is not a
 * cosmetic defect.
 */
const NEW_LEAF_APPLY = {
  /** Appended to the JobPosting description. New Leaf's market and segment. */
  marketClause: ' serving Tampa Bay homes',
  /** Appended to the meta description. */
  metaClause: ' Tampa Bay residential cleaning.',
  /** The manifesto eyebrow. */
  eyebrow: 'Now hiring · Tampa Bay',
  /**
   * The manifesto paragraph. Both a pun on the company's own name and a
   * residential premise ("people's homes", "every home we walk into") — wrong
   * on both counts for a commercial cleaner hiring through the same page.
   */
  manifesto:
    "We exist to bring life back into people's homes — and into the lives of the people " +
    'who do the work. Every home we walk into gets a new leaf turned. Every person on ' +
    'this team gets the same.',
  /** The registered entity named in the photo-release consent. */
  legalEntity: 'New Leaf Cleaning, LLC',
  /**
   * Appended to the submitted-screen reassurance. Names New Leaf's founder, so
   * it is wrong on every other tenant's hiring page — gated on isNewLeaf.
   */
  founderClause: ' — Vikelas will read yours himself.',
  /**
   * ⚠️ New Leaf's own address. It was being asserted as the work location of
   * EVERY tenant's job posting. Omitted entirely for anyone else — there is no
   * per-tenant service-area model to substitute, and a guessed one would be
   * worse than none.
   */
  jobLocation: {
    '@type': 'Place',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'St. Petersburg',
      addressRegion: 'FL',
      postalCode: '33713',
      addressCountry: 'US',
    },
  },
} as const;

// ---------------------------------------------------------------------------
// Meta — SEO + JobPosting JSON-LD
//
// This is a hiring page every tenant links applicants to from Indeed and
// their own job posts, not just New Leaf's. It was hard-coding "New Leaf
// Cleaning" into the title, description, and — worse — the JobPosting
// structured data's hiringOrganization, so Google (and any applicant who
// read the page source) was told every tenant's cleaning-technician opening
// belonged to New Leaf. `applicationContact.email` is included only when
// this tenant has actually set a contact address; a wrong address is worse
// than an omitted one for a channel Google may show job-seekers directly.
// ---------------------------------------------------------------------------
export const meta: MetaFunction = ({ matches }) => {
  const branding = brandingFromMatches(matches);
  /*
   * The canonical + og:url must name the domain being served. `SITE.url` is
   * nlcstpete.com, so every tenant's hiring page declared New Leaf's domain as
   * its own canonical — which tells a search engine the two pages are the same
   * page, and the one that wins is New Leaf's.
   */
  const url = `${branding.origin}/apply`;
  /*
   * New Leaf's OG card, with New Leaf's artwork on it. Omitted rather than
   * substituted for anyone else: a share preview showing another company's
   * image is exactly the co-branded look this page is being fixed for.
   */
  const ogImage = branding.isNewLeaf ? `${branding.origin}/images/og-image.png` : null;
  /*
   * A logo for the structured data, only when this company actually has one.
   * Data URIs (what the setup wizard currently stores) are skipped — schema.org
   * consumers want a fetchable URL, and an unusable one is worse than none.
   */
  const schemaLogo = branding.logoUrl?.startsWith('/')
    ? `${branding.origin}${branding.logoUrl}`
    : branding.logoUrl?.startsWith('http')
      ? branding.logoUrl
      : null;
  const today = new Date().toISOString().split('T')[0];
  const validThrough = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0];

  const jobPosting = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: 'Cleaning Technician — Full-Time W-2',
    /*
     * The market clause is New Leaf's market and New Leaf's segment. Second
     * Shift is a commercial cleaner somewhere else, so the line described the
     * wrong work in the wrong place under the right name.
     */
    description:
      `Join ${branding.name} as a full-time W-2 cleaning technician` +
      `${branding.isNewLeaf ? NEW_LEAF_APPLY.marketClause : ''}. ` +
      'Real benefits, real pay, real care for the people who do the work.',
    datePosted: today,
    validThrough: validThrough,
    employmentType: 'FULL_TIME',
    hiringOrganization: {
      '@type': 'Organization',
      name: branding.name,
      sameAs: branding.origin,
      ...(schemaLogo && { logo: schemaLogo }),
    },
    /*
     * See NEW_LEAF_APPLY.jobLocation. Omitted for every other tenant —
     * `applicantLocationRequirements` below still scopes the posting to the US.
     */
    ...(branding.isNewLeaf && { jobLocation: NEW_LEAF_APPLY.jobLocation }),
    applicantLocationRequirements: {
      '@type': 'Country',
      name: 'United States',
    },
    directApply: true,
    ...(branding.email && {
      applicationContact: {
        '@type': 'ContactPoint',
        email: branding.email,
      },
    }),
  };

  return [
    { title: `Cleaning Technician Application | ${branding.name}` },
    {
      name: 'description',
      content:
        `Apply to join ${branding.name}. W-2 employment, real benefits, ` +
        'and a team that actually has your back.' +
        `${branding.isNewLeaf ? NEW_LEAF_APPLY.metaClause : ''}`,
    },
    { name: 'robots', content: 'index, follow' },
    { property: 'og:title', content: `Join ${branding.name} — Cleaning Technician` },
    { property: 'og:description', content: 'W-2 employment, real benefits, real care.' },
    { property: 'og:url', content: url },
    { property: 'og:type', content: 'website' },
    ...(ogImage
      ? [
          { property: 'og:image', content: ogImage },
          { name: 'twitter:card', content: 'summary_large_image' },
          { name: 'twitter:image', content: ogImage },
        ]
      : [{ name: 'twitter:card', content: 'summary' }]),
    { tagName: 'link', rel: 'canonical', href: url },
    {
      'script:ld+json': jobPosting,
    },
  ];
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Step = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
type PageState = 'loading' | 'form' | 'submitted' | 'expired';

interface ResumePayload {
  step: number;
  data: Partial<ApplicantSubmitPayload>;
  expiresAt: number | null;
}

const DEFAULT_REFERENCE: ApplicantReference = {
  name: '',
  phone: '',
  relationship: '',
  yearsKnown: 0,
  occupation: '',
  email: '',
};

const EXPERIENCE_LABELS: Record<ApplicantExperienceType, string> = {
  house_cleaning: 'House cleaning',
  janitorial: 'Janitorial',
  homemaker: 'Homemaker / housekeeping',
  str: 'Short-term rentals (Airbnb)',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ApplyPage() {
  // The hiring page speaks AS the company. SITE is New Leaf. See ~/lib/branding.
  const branding = useBranding();
  const [pageState, setPageState] = useState<PageState>('form');
  const [token, setToken] = useState<string | null>(null);
  // The token as a ref as well as state: `ensureToken()` is called from async
  // handlers that captured an older render, and a stale `token` closure there
  // would open a second draft for the same applicant.
  const tokenRef = useRef<string | null>(null);
  const startingRef = useRef<Promise<string | null> | null>(null);
  const [step, setStep] = useState<Step>(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [mailingAddress, setMailingAddress] = useState('');
  const [physicalSameAsMailing, setPhysicalSameAsMailing] = useState(true);
  const [physicalAddress, setPhysicalAddress] = useState('');

  const [workAuthorization, setWorkAuthorization] = useState<boolean | null>(null);
  const [validLicense, setValidLicense] = useState<boolean | null>(null);
  const [hasVehicle, setHasVehicle] = useState<boolean | null>(null);
  const [vehicleInsured, setVehicleInsured] = useState<boolean | null>(null);
  const [canLift25, setCanLift25] = useState<boolean | null>(null);
  const [noMobilityIssues, setNoMobilityIssues] = useState<boolean | null>(null);
  const [allergicChemicals, setAllergicChemicals] = useState<boolean | null>(null);
  const [allergicPets, setAllergicPets] = useState<boolean | null>(null);
  const [afraidPets, setAfraidPets] = useState<boolean | null>(null);
  const [canWorkMF96, setCanWorkMF96] = useState<boolean | null>(null);
  const [criminalHistory, setCriminalHistory] = useState<boolean | null>(null);
  const [criminalHistoryText, setCriminalHistoryText] = useState('');

  const [experienceTypes, setExperienceTypes] = useState<ApplicantExperienceType[]>([]);
  const [yearsExperience, setYearsExperience] = useState<number | ''>('');
  const [previousEmployer, setPreviousEmployer] = useState('');

  const [references, setReferences] = useState<ApplicantReference[]>([
    { ...DEFAULT_REFERENCE },
    { ...DEFAULT_REFERENCE },
    { ...DEFAULT_REFERENCE },
  ]);

  const [narrativeBringLife, setNarrativeBringLife] = useState('');
  const [narrativeCostlyService, setNarrativeCostlyService] = useState('');
  const [narrativeYearFromNow, setNarrativeYearFromNow] = useState('');

  const [appearanceReleaseSigned, setAppearanceReleaseSigned] = useState(false);
  const [applicantSignature, setApplicantSignature] = useState('');
  const [confirmAccurate, setConfirmAccurate] = useState(false);

  const [sourceChannel, setSourceChannel] = useState('');

  // Honeypot — bots fill this, humans never see it.
  const [website, setWebsite] = useState('');

  // ---------------- Init: resume an existing draft, or wait -----------------
  //
  // Opening the page no longer opens a draft. It used to: this effect called
  // `startNewApplication()` on mount, which meant every page view cost a row in
  // `applicants` AND a slot against the 3-per-hour limit on POST /api/apply.
  // Somebody who loaded the form, backed out, and came back had spent two of
  // their three before typing a character — and the lockout only became visible
  // at step 8, on the click that mattered. The table filled with nameless
  // drafts, which is the same bug leaving litter.
  //
  // The draft is opened on the first Next instead, so the limit now counts
  // applications rather than page views.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const resumeToken = params.get('resume');
    if (resumeToken) void resumeApplication(resumeToken);
  }, []);

  /**
   * The token for this application, opening a draft the first time one is
   * needed. Returns null if the server would not give us one.
   *
   * `startingRef` holds the in-flight promise, not a boolean: two callers in the
   * same tick (React 18 StrictMode double-invokes effects, and Next can race a
   * blur handler) must await the SAME request. A boolean guard would let the
   * second caller through as soon as the first set it, which is how one
   * applicant ends up with two drafts and two slots consumed.
   */
  async function ensureToken(): Promise<string | null> {
    if (tokenRef.current) return tokenRef.current;
    if (startingRef.current) return startingRef.current;

    startingRef.current = (async () => {
      try {
        const res = await fetch('/api/apply', { method: 'POST' });
        const data = (await res.json()) as { ok: boolean; data?: { token: string }; error?: string };
        if (data.ok && data.data?.token) {
          tokenRef.current = data.data.token;
          setToken(data.data.token);
          return data.data.token;
        }
        setError(data.error || 'Could not start application');
        return null;
      } catch {
        setError('Network error — please refresh and try again');
        return null;
      } finally {
        // Cleared either way: a failure must not permanently wedge the form.
        startingRef.current = null;
      }
    })();

    return startingRef.current;
  }

  async function resumeApplication(resumeToken: string) {
    try {
      const res = await fetch(`/api/apply/${encodeURIComponent(resumeToken)}`);
      const data = (await res.json()) as {
        ok: boolean;
        data?: ResumePayload;
        error?: string;
        code?: string;
      };
      if (!data.ok) {
        if (data.code === 'ALREADY_SUBMITTED') {
          setPageState('submitted');
          return;
        }
        setPageState('expired');
        setError(data.error || 'Application link expired');
        return;
      }
      tokenRef.current = resumeToken;
      setToken(resumeToken);
      hydrateForm(data.data);
      setStep(Math.min(Math.max(data.data?.step ?? 1, 1), 8) as Step);
    } catch {
      setError('Network error — please refresh and try again');
    }
  }

  function hydrateForm(payload?: ResumePayload) {
    const d = payload?.data ?? {};
    if (d.fullName !== undefined) setFullName(d.fullName);
    if (d.email !== undefined) setEmail(d.email);
    if (d.phone !== undefined) setPhone(d.phone);
    if (d.mailingAddress !== undefined) setMailingAddress(d.mailingAddress);
    if (d.physicalAddress !== undefined) {
      setPhysicalAddress(d.physicalAddress);
      setPhysicalSameAsMailing(d.physicalAddress === d.mailingAddress);
    }
    if (d.workAuthorization !== undefined) setWorkAuthorization(d.workAuthorization ?? null);
    if (d.validLicense !== undefined) setValidLicense(d.validLicense ?? null);
    if (d.hasVehicle !== undefined) setHasVehicle(d.hasVehicle ?? null);
    if (d.vehicleInsured !== undefined) setVehicleInsured(d.vehicleInsured ?? null);
    if (d.canLift25 !== undefined) setCanLift25(d.canLift25 ?? null);
    if (d.noMobilityIssues !== undefined) setNoMobilityIssues(d.noMobilityIssues ?? null);
    if (d.allergicChemicals !== undefined) setAllergicChemicals(d.allergicChemicals ?? null);
    if (d.allergicPets !== undefined) setAllergicPets(d.allergicPets ?? null);
    if (d.afraidPets !== undefined) setAfraidPets(d.afraidPets ?? null);
    if (d.canWorkMF96 !== undefined) setCanWorkMF96(d.canWorkMF96 ?? null);
    if (d.criminalHistory !== undefined) setCriminalHistory(d.criminalHistory ?? null);
    if (d.criminalHistoryText !== undefined) setCriminalHistoryText(d.criminalHistoryText);
    if (d.experienceTypes !== undefined) setExperienceTypes(d.experienceTypes ?? []);
    if (d.yearsExperience !== undefined) setYearsExperience(d.yearsExperience ?? '');
    if (d.previousEmployer !== undefined) setPreviousEmployer(d.previousEmployer);
    if (d.references !== undefined && d.references.length > 0) {
      const padded = [...d.references];
      while (padded.length < 3) padded.push({ ...DEFAULT_REFERENCE });
      setReferences(padded.slice(0, 3));
    }
    if (d.narrativeBringLife !== undefined) setNarrativeBringLife(d.narrativeBringLife);
    if (d.narrativeCostlyService !== undefined) setNarrativeCostlyService(d.narrativeCostlyService);
    if (d.narrativeYearFromNow !== undefined) setNarrativeYearFromNow(d.narrativeYearFromNow);
    if (d.appearanceReleaseSigned !== undefined) setAppearanceReleaseSigned(d.appearanceReleaseSigned);
    if (d.sourceChannel !== undefined) setSourceChannel(d.sourceChannel);
  }

  // ---------------- Save progress (fire-and-forget on Next) ----------------
  const saveProgress = useCallback(
    async (nextStep: number) => {
      // The first Next is what opens the draft — see `ensureToken()` above.
      const active = await ensureToken();
      if (!active) return;
      const payload = buildProgressPayload(nextStep);
      try {
        await fetch(`/api/apply/${encodeURIComponent(active)}/progress`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } catch {
        // Silent — best-effort save. Submit will catch real failures.
      }
    },
    [
      token,
      fullName, email, phone, mailingAddress, physicalAddress, physicalSameAsMailing,
      workAuthorization, validLicense, hasVehicle, vehicleInsured, canLift25,
      noMobilityIssues, allergicChemicals, allergicPets, afraidPets, canWorkMF96,
      criminalHistory, criminalHistoryText,
      experienceTypes, yearsExperience, previousEmployer, references,
      narrativeBringLife, narrativeCostlyService, narrativeYearFromNow,
      appearanceReleaseSigned, sourceChannel,
    ]
  );

  function buildProgressPayload(nextStep: number): ApplicantProgressPayload {
    const physical = physicalSameAsMailing ? mailingAddress : physicalAddress;
    return {
      step: nextStep,
      fullName: fullName.trim() || undefined,
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      mailingAddress: mailingAddress.trim() || undefined,
      physicalAddress: physical.trim() || undefined,
      workAuthorization: workAuthorization ?? undefined,
      validLicense: validLicense ?? undefined,
      hasVehicle: hasVehicle ?? undefined,
      vehicleInsured: vehicleInsured ?? undefined,
      canLift25: canLift25 ?? undefined,
      noMobilityIssues: noMobilityIssues ?? undefined,
      allergicChemicals: allergicChemicals ?? undefined,
      allergicPets: allergicPets ?? undefined,
      afraidPets: afraidPets ?? undefined,
      canWorkMF96: canWorkMF96 ?? undefined,
      criminalHistory: criminalHistory ?? undefined,
      criminalHistoryText: criminalHistoryText.trim() || undefined,
      experienceTypes: experienceTypes.length ? experienceTypes : undefined,
      yearsExperience: typeof yearsExperience === 'number' ? yearsExperience : undefined,
      previousEmployer: previousEmployer.trim() || undefined,
      references: references.some((r) => r.name || r.phone) ? references : undefined,
      narrativeBringLife: narrativeBringLife.trim() || undefined,
      narrativeCostlyService: narrativeCostlyService.trim() || undefined,
      narrativeYearFromNow: narrativeYearFromNow.trim() || undefined,
      appearanceReleaseSigned: appearanceReleaseSigned || undefined,
      sourceChannel: sourceChannel.trim() || undefined,
    };
  }

  // ---------------- Step navigation ----------------
  function goToStep(s: Step) {
    setError(null);
    setStep(s);
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function next() {
    const validation = validateCurrentStep();
    if (validation) {
      setError(validation);
      return;
    }
    const nextStep = Math.min(step + 1, 8) as Step;
    void saveProgress(nextStep);
    goToStep(nextStep);
  }

  function back() {
    const prev = Math.max(step - 1, 1) as Step;
    goToStep(prev);
  }

  function validateCurrentStep(): string | null {
    switch (step) {
      case 1: {
        if (!fullName.trim() || fullName.trim().length < 2) return 'Please enter your full name.';
        if (!isValidEmail(email)) return 'Please enter a valid email address.';
        if (phone.trim().length < 7) return 'Please enter your cell phone number.';
        if (mailingAddress.trim().length < 5) return 'Please enter your mailing address.';
        if (!physicalSameAsMailing && physicalAddress.trim().length < 5) return 'Please enter your physical address.';
        return null;
      }
      case 2: {
        const required = [
          [workAuthorization, 'work authorization'],
          [validLicense, "driver's license"],
          [hasVehicle, 'reliable vehicle'],
          [vehicleInsured, 'vehicle insurance'],
          [canLift25, 'lifting requirement'],
          [noMobilityIssues, 'mobility'],
          [allergicChemicals, 'chemical allergies'],
          [allergicPets, 'pet allergies'],
          [afraidPets, 'comfort with pets'],
          [canWorkMF96, 'work hours'],
          [criminalHistory, 'criminal history'],
        ] as const;
        for (const [val, label] of required) {
          if (val === null) return `Please answer the ${label} question.`;
        }
        if (criminalHistory && criminalHistoryText.trim().length < 5) {
          return 'Please briefly describe so we can review.';
        }
        return null;
      }
      case 3: {
        if (typeof yearsExperience !== 'number' || yearsExperience < 0) {
          return 'Please enter your years of cleaning experience (0 is fine — everyone starts somewhere).';
        }
        return null;
      }
      case 4: {
        for (let i = 0; i < 3; i++) {
          const r = references[i];
          if (!r || !r.name.trim() || !r.phone.trim() || !r.relationship.trim()) {
            return `Please complete reference #${i + 1}.`;
          }
        }
        return null;
      }
      case 5: {
        if (narrativeBringLife.trim().length < 5) return 'A short answer is enough — but please share something.';
        if (narrativeCostlyService.trim().length < 5) return 'Please share a story about service that cost you something.';
        if (narrativeYearFromNow.trim().length < 5) return 'Please share what a good year here would mean for you.';
        return null;
      }
      case 6:
        return null; // appearance release optional
      case 7:
        return null;
      case 8: {
        if (applicantSignature.trim().length < 2) return 'Please type your full name to sign.';
        if (!confirmAccurate) return 'Please confirm the information is accurate.';
        return null;
      }
      default:
        return null;
    }
  }

  // ---------------- Submit ----------------
  async function handleSubmit() {
    /*
     * Never a silent return. This used to be `if (!token) return;` — so on the
     * one click that matters, with eight steps already filled in, a missing
     * token did nothing at all: no error, no spinner, no request. Now the draft
     * is opened here if it does not exist, and if it cannot be, the applicant
     * is told rather than left tapping a dead button.
     */
    const active = await ensureToken();
    if (!active) {
      setError(
        'We could not open an application slot just now. Please try again in a moment — nothing you typed has been lost.',
      );
      return;
    }
    const validation = validateCurrentStep();
    if (validation) {
      setError(validation);
      return;
    }
    setSubmitting(true);
    setError(null);

    const payload: ApplicantSubmitPayload = {
      fullName: fullName.trim(),
      email: email.trim().toLowerCase(),
      phone: phone.trim(),
      mailingAddress: mailingAddress.trim(),
      physicalAddress: (physicalSameAsMailing ? mailingAddress : physicalAddress).trim(),
      workAuthorization: workAuthorization ?? false,
      validLicense: validLicense ?? false,
      hasVehicle: hasVehicle ?? false,
      vehicleInsured: vehicleInsured ?? false,
      canLift25: canLift25 ?? false,
      noMobilityIssues: noMobilityIssues ?? false,
      allergicChemicals: allergicChemicals ?? false,
      allergicPets: allergicPets ?? false,
      afraidPets: afraidPets ?? false,
      canWorkMF96: canWorkMF96 ?? false,
      criminalHistory: criminalHistory ?? false,
      criminalHistoryText: criminalHistoryText.trim() || undefined,
      experienceTypes,
      yearsExperience: typeof yearsExperience === 'number' ? yearsExperience : 0,
      previousEmployer: previousEmployer.trim(),
      references,
      narrativeBringLife: narrativeBringLife.trim(),
      narrativeCostlyService: narrativeCostlyService.trim(),
      narrativeYearFromNow: narrativeYearFromNow.trim(),
      appearanceReleaseSigned,
      applicantSignature: applicantSignature.trim(),
      sourceChannel: sourceChannel.trim(),
      website,
    };

    try {
      const res = await fetch(`/api/apply/${encodeURIComponent(active)}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = (await res.json()) as { ok: boolean; data?: { id: string }; error?: string };
      if (data.ok) {
        setPageState('submitted');
      } else {
        setError(data.error || 'Submission failed');
        setSubmitting(false);
      }
    } catch {
      setError('Network error — please try again.');
      setSubmitting(false);
    }
  }

  // ---------------- Save & continue later ----------------
  async function copyResumeLink() {
    if (typeof window === 'undefined') return;
    // Asking to save IS the intent to apply, so this opens the draft too — a
    // "save and continue later" button that copies nothing is a broken promise.
    const active = await ensureToken();
    if (!active) return;
    const url = `${window.location.origin}/apply?resume=${active}`;
    void navigator.clipboard.writeText(url);
    alert('Resume link copied to clipboard. Save it somewhere safe — it works for 30 days.');
  }

  // ---------------- Render ----------------
  if (pageState === 'submitted') {
    return <SubmittedScreen fullName={fullName} />;
  }
  if (pageState === 'expired') {
    return <ExpiredScreen />;
  }

  return (
    <div className="apply-page">
      <Manifesto />

      <div className="apply-shell">
        <div className="apply-card">
          <ApplyStepIndicator currentStep={step} />
          {error && <div className="apply-error" role="alert">{error}</div>}

          {step === 1 && (
            <Step1Hello
              fullName={fullName} setFullName={setFullName}
              email={email} setEmail={setEmail}
              phone={phone} setPhone={setPhone}
              mailingAddress={mailingAddress} setMailingAddress={setMailingAddress}
              physicalSameAsMailing={physicalSameAsMailing} setPhysicalSameAsMailing={setPhysicalSameAsMailing}
              physicalAddress={physicalAddress} setPhysicalAddress={setPhysicalAddress}
              sourceChannel={sourceChannel} setSourceChannel={setSourceChannel}
              website={website} setWebsite={setWebsite}
            />
          )}
          {step === 2 && (
            <Step2Eligibility
              workAuthorization={workAuthorization} setWorkAuthorization={setWorkAuthorization}
              validLicense={validLicense} setValidLicense={setValidLicense}
              hasVehicle={hasVehicle} setHasVehicle={setHasVehicle}
              vehicleInsured={vehicleInsured} setVehicleInsured={setVehicleInsured}
              canLift25={canLift25} setCanLift25={setCanLift25}
              noMobilityIssues={noMobilityIssues} setNoMobilityIssues={setNoMobilityIssues}
              allergicChemicals={allergicChemicals} setAllergicChemicals={setAllergicChemicals}
              allergicPets={allergicPets} setAllergicPets={setAllergicPets}
              afraidPets={afraidPets} setAfraidPets={setAfraidPets}
              canWorkMF96={canWorkMF96} setCanWorkMF96={setCanWorkMF96}
              criminalHistory={criminalHistory} setCriminalHistory={setCriminalHistory}
              criminalHistoryText={criminalHistoryText} setCriminalHistoryText={setCriminalHistoryText}
            />
          )}
          {step === 3 && (
            <Step3Experience
              experienceTypes={experienceTypes} setExperienceTypes={setExperienceTypes}
              yearsExperience={yearsExperience} setYearsExperience={setYearsExperience}
              previousEmployer={previousEmployer} setPreviousEmployer={setPreviousEmployer}
            />
          )}
          {step === 4 && (
            <Step4References references={references} setReferences={setReferences} />
          )}
          {step === 5 && (
            <Step5Heart
              bringLife={narrativeBringLife} setBringLife={setNarrativeBringLife}
              costlyService={narrativeCostlyService} setCostlyService={setNarrativeCostlyService}
              yearFromNow={narrativeYearFromNow} setYearFromNow={setNarrativeYearFromNow}
            />
          )}
          {step === 6 && (
            <Step6Appearance signed={appearanceReleaseSigned} setSigned={setAppearanceReleaseSigned} />
          )}
          {step === 7 && (
            <Step7Review
              fullName={fullName}
              email={email}
              phone={phone}
              mailingAddress={mailingAddress}
              experienceTypes={experienceTypes}
              yearsExperience={yearsExperience}
            />
          )}
          {step === 8 && (
            <Step8Sign
              applicantSignature={applicantSignature} setApplicantSignature={setApplicantSignature}
              confirmAccurate={confirmAccurate} setConfirmAccurate={setConfirmAccurate}
            />
          )}

          <ApplyNavButtons
            step={step}
            canBack={step > 1}
            isSubmitting={submitting}
            onBack={back}
            onNext={next}
            onSubmit={handleSubmit}
          />

          {step >= 2 && token && (
            <button type="button" className="apply-save-link" onClick={copyResumeLink}>
              Save &amp; continue later
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ===================================================================
// Manifesto
// ===================================================================
function Manifesto() {
  // "We're New Leaf." was a literal, shown to applicants of every other company
  // on the platform. It is the first line of the page and names the employer.
  const branding = useBranding();
  return (
    <header className="apply-manifesto">
      <div className="apply-manifesto-inner">
        {/* The market is New Leaf's. Another company hiring through this page
            recruits somewhere else, so the eyebrow drops the region rather than
            naming the wrong one. */}
        <p className="apply-manifesto-eyebrow">
          {branding.isNewLeaf ? NEW_LEAF_APPLY.eyebrow : 'Now hiring'}
        </p>
        <h1 className="apply-manifesto-title">We're {branding.name}.</h1>
        <p className="apply-manifesto-body">
          {branding.isNewLeaf
            ? NEW_LEAF_APPLY.manifesto
            : 'We exist to do the work properly — and to look after the people who do it. ' +
              'Every place we walk into is left better than we found it. Every person on ' +
              'this team gets the same.'}
        </p>
        <p className="apply-manifesto-body apply-manifesto-emphasis">
          We serve, even when it costs us. If that lands with you, we'd love to read your story.
        </p>
      </div>
    </header>
  );
}

// ===================================================================
// Step indicator
// ===================================================================
function ApplyStepIndicator({ currentStep }: { currentStep: Step }) {
  return (
    <div className="apply-steps">
      {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
        <div
          key={n}
          className={`apply-step-dot ${n === currentStep ? 'apply-step-dot-current' : ''} ${n < currentStep ? 'apply-step-dot-done' : ''}`}
          aria-label={`Step ${n}`}
        />
      ))}
    </div>
  );
}

// ===================================================================
// Steps
// ===================================================================

interface YesNoProps {
  label: string;
  helper?: string;
  value: boolean | null;
  onChange: (v: boolean) => void;
}

function YesNo({ label, helper, value, onChange }: YesNoProps) {
  return (
    <div className="apply-yesno">
      <div className="apply-yesno-label">
        <span>{label}</span>
        {helper && <span className="apply-yesno-helper">{helper}</span>}
      </div>
      <div className="apply-yesno-buttons">
        <button
          type="button"
          className={`apply-yesno-btn ${value === true ? 'apply-yesno-btn-active' : ''}`}
          onClick={() => onChange(true)}
        >Yes</button>
        <button
          type="button"
          className={`apply-yesno-btn ${value === false ? 'apply-yesno-btn-active' : ''}`}
          onClick={() => onChange(false)}
        >No</button>
      </div>
    </div>
  );
}

interface Step1Props {
  fullName: string; setFullName: (v: string) => void;
  email: string; setEmail: (v: string) => void;
  phone: string; setPhone: (v: string) => void;
  mailingAddress: string; setMailingAddress: (v: string) => void;
  physicalSameAsMailing: boolean; setPhysicalSameAsMailing: (v: boolean) => void;
  physicalAddress: string; setPhysicalAddress: (v: string) => void;
  sourceChannel: string; setSourceChannel: (v: string) => void;
  website: string; setWebsite: (v: string) => void;
}

function Step1Hello(p: Step1Props) {
  return (
    <>
      <h2 className="apply-step-title">Hello, who are you?</h2>
      <p className="apply-step-body">
        Just the basics so we can get back to you. We hire for fit, not boxes — be yourself.
      </p>
      <div className="apply-form-group">
        <label htmlFor="fullName">Full name</label>
        <input id="fullName" type="text" value={p.fullName} onChange={(e) => p.setFullName(e.target.value)} autoComplete="name" />
      </div>
      <div className="apply-form-group">
        <label htmlFor="email">Email</label>
        <input id="email" type="email" value={p.email} onChange={(e) => p.setEmail(e.target.value)} autoComplete="email" inputMode="email" />
      </div>
      <div className="apply-form-group">
        <label htmlFor="phone">Cell phone</label>
        <input id="phone" type="tel" value={p.phone} onChange={(e) => p.setPhone(formatPhoneInput(e.target.value))} autoComplete="tel" inputMode="tel" />
      </div>
      <div className="apply-form-group">
        <label htmlFor="mailing">Mailing address</label>
        <textarea id="mailing" rows={2} value={p.mailingAddress} onChange={(e) => p.setMailingAddress(e.target.value)} autoComplete="street-address" />
      </div>
      <div className="apply-form-group apply-checkbox-row">
        <input
          id="sameAsMailing"
          type="checkbox"
          checked={p.physicalSameAsMailing}
          onChange={(e) => p.setPhysicalSameAsMailing(e.target.checked)}
        />
        <label htmlFor="sameAsMailing">Physical address is the same as mailing</label>
      </div>
      {!p.physicalSameAsMailing && (
        <div className="apply-form-group">
          <label htmlFor="physical">Physical address</label>
          <textarea id="physical" rows={2} value={p.physicalAddress} onChange={(e) => p.setPhysicalAddress(e.target.value)} />
        </div>
      )}
      <div className="apply-form-group">
        <label htmlFor="source">How did you hear about us? <span className="apply-optional">optional</span></label>
        <input id="source" type="text" value={p.sourceChannel} onChange={(e) => p.setSourceChannel(e.target.value)} placeholder="Indeed, friend, walking by..." />
      </div>
      {/* Honeypot — invisible to humans, filled by bots. */}
      <div className="apply-honeypot" aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input id="website" type="text" tabIndex={-1} autoComplete="off" value={p.website} onChange={(e) => p.setWebsite(e.target.value)} />
      </div>
    </>
  );
}

interface Step2Props {
  workAuthorization: boolean | null; setWorkAuthorization: (v: boolean) => void;
  validLicense: boolean | null; setValidLicense: (v: boolean) => void;
  hasVehicle: boolean | null; setHasVehicle: (v: boolean) => void;
  vehicleInsured: boolean | null; setVehicleInsured: (v: boolean) => void;
  canLift25: boolean | null; setCanLift25: (v: boolean) => void;
  noMobilityIssues: boolean | null; setNoMobilityIssues: (v: boolean) => void;
  allergicChemicals: boolean | null; setAllergicChemicals: (v: boolean) => void;
  allergicPets: boolean | null; setAllergicPets: (v: boolean) => void;
  afraidPets: boolean | null; setAfraidPets: (v: boolean) => void;
  canWorkMF96: boolean | null; setCanWorkMF96: (v: boolean) => void;
  criminalHistory: boolean | null; setCriminalHistory: (v: boolean) => void;
  criminalHistoryText: string; setCriminalHistoryText: (v: string) => void;
}

function Step2Eligibility(p: Step2Props) {
  return (
    <>
      <h2 className="apply-step-title">A few honest yes/nos</h2>
      <p className="apply-step-body">
        These keep us legal and keep you safe on the job. There are no wrong answers — we just need the truth.
      </p>
      <YesNo label="Are you authorized to work in the U.S.?" value={p.workAuthorization} onChange={p.setWorkAuthorization} />
      <YesNo label="Do you have a valid driver's license?" value={p.validLicense} onChange={p.setValidLicense} />
      <YesNo label="Do you have full-time access to a reliable vehicle?" value={p.hasVehicle} onChange={p.setHasVehicle} />
      <YesNo label="Is your vehicle insured?" value={p.vehicleInsured} onChange={p.setVehicleInsured} />
      <YesNo label="Can you lift 25 pounds or more?" value={p.canLift25} onChange={p.setCanLift25} />
      <YesNo label="Can you stand, bend, and kneel without difficulty?" value={p.noMobilityIssues} onChange={p.setNoMobilityIssues} />
      <YesNo label="Are you allergic to common cleaning chemicals?" value={p.allergicChemicals} onChange={p.setAllergicChemicals} />
      <YesNo label="Are you allergic to cats or dogs?" value={p.allergicPets} onChange={p.setAllergicPets} />
      <YesNo label="Are you afraid of cats or dogs?" value={p.afraidPets} onChange={p.setAfraidPets} />
      <YesNo label="Can you work Monday–Friday, 9 AM–6 PM?" helper="Some flex is fine — let us know in the heart questions." value={p.canWorkMF96} onChange={p.setCanWorkMF96} />
      <YesNo label="Have you been convicted of a criminal offense?" helper="We run background checks after offer. A 'yes' is not automatic disqualification." value={p.criminalHistory} onChange={p.setCriminalHistory} />
      {p.criminalHistory && (
        <div className="apply-form-group">
          <label htmlFor="crimHistText">Briefly describe</label>
          <textarea id="crimHistText" rows={3} value={p.criminalHistoryText} onChange={(e) => p.setCriminalHistoryText(e.target.value)} />
        </div>
      )}
    </>
  );
}

interface Step3Props {
  experienceTypes: ApplicantExperienceType[]; setExperienceTypes: (v: ApplicantExperienceType[]) => void;
  yearsExperience: number | ''; setYearsExperience: (v: number | '') => void;
  previousEmployer: string; setPreviousEmployer: (v: string) => void;
}

function Step3Experience(p: Step3Props) {
  const branding = useBranding();
  function toggle(t: ApplicantExperienceType) {
    if (p.experienceTypes.includes(t)) {
      p.setExperienceTypes(p.experienceTypes.filter((x) => x !== t));
    } else {
      p.setExperienceTypes([...p.experienceTypes, t]);
    }
  }
  return (
    <>
      <h2 className="apply-step-title">Your experience</h2>
      <p className="apply-step-body">
        We train you on the {branding.name} way regardless. This just helps us know where you're starting.
      </p>
      <div className="apply-form-group">
        <span className="apply-checkbox-group-label">Have you done any of these? Check all that apply:</span>
        {(Object.keys(EXPERIENCE_LABELS) as ApplicantExperienceType[]).map((t) => (
          <label key={t} className="apply-checkbox-row">
            <input
              type="checkbox"
              checked={p.experienceTypes.includes(t)}
              onChange={() => toggle(t)}
            />
            <span>{EXPERIENCE_LABELS[t]}</span>
          </label>
        ))}
      </div>
      <div className="apply-form-group">
        <label htmlFor="years">Years of cleaning experience (0 is fine)</label>
        <input
          id="years"
          type="number"
          min={0}
          max={60}
          inputMode="numeric"
          value={p.yearsExperience}
          onChange={(e) => p.setYearsExperience(e.target.value === '' ? '' : Number(e.target.value))}
        />
      </div>
      <div className="apply-form-group">
        <label htmlFor="prev">Previous employer <span className="apply-optional">optional</span></label>
        <input id="prev" type="text" value={p.previousEmployer} onChange={(e) => p.setPreviousEmployer(e.target.value)} />
      </div>
    </>
  );
}

interface Step4Props {
  references: ApplicantReference[];
  setReferences: (v: ApplicantReference[]) => void;
}

function Step4References(p: Step4Props) {
  function update(idx: number, field: keyof ApplicantReference, value: string | number) {
    const next = [...p.references];
    const existing = next[idx] ?? { ...DEFAULT_REFERENCE };
    next[idx] = { ...existing, [field]: value };
    p.setReferences(next);
  }
  return (
    <>
      <h2 className="apply-step-title">Three references</h2>
      <p className="apply-step-body">
        People who've known you at least three years and aren't related to you. We may reach out — and we promise to be quick about it.
      </p>
      {p.references.map((ref, idx) => (
        <fieldset key={idx} className="apply-reference-card">
          <legend>Reference #{idx + 1}</legend>
          <div className="apply-form-group">
            <label htmlFor={`r${idx}-name`}>Name</label>
            <input id={`r${idx}-name`} type="text" value={ref.name} onChange={(e) => update(idx, 'name', e.target.value)} />
          </div>
          <div className="apply-form-group">
            <label htmlFor={`r${idx}-phone`}>Phone</label>
            <input id={`r${idx}-phone`} type="tel" value={ref.phone} onChange={(e) => update(idx, 'phone', formatPhoneInput(e.target.value))} />
          </div>
          <div className="apply-form-group">
            <label htmlFor={`r${idx}-email`}>Email <span className="apply-optional">optional, but helpful</span></label>
            <input id={`r${idx}-email`} type="email" value={ref.email} onChange={(e) => update(idx, 'email', e.target.value)} />
          </div>
          <div className="apply-form-group apply-form-row">
            <div>
              <label htmlFor={`r${idx}-rel`}>Relationship</label>
              <input id={`r${idx}-rel`} type="text" value={ref.relationship} onChange={(e) => update(idx, 'relationship', e.target.value)} placeholder="Former manager, friend..." />
            </div>
            <div>
              <label htmlFor={`r${idx}-yrs`}>Years known</label>
              <input id={`r${idx}-yrs`} type="number" min={0} max={99} value={ref.yearsKnown} onChange={(e) => update(idx, 'yearsKnown', Number(e.target.value))} />
            </div>
          </div>
          <div className="apply-form-group">
            <label htmlFor={`r${idx}-occ`}>Occupation <span className="apply-optional">optional</span></label>
            <input id={`r${idx}-occ`} type="text" value={ref.occupation} onChange={(e) => update(idx, 'occupation', e.target.value)} />
          </div>
        </fieldset>
      ))}
    </>
  );
}

interface Step5Props {
  bringLife: string; setBringLife: (v: string) => void;
  costlyService: string; setCostlyService: (v: string) => void;
  yearFromNow: string; setYearFromNow: (v: string) => void;
}

function Step5Heart(p: Step5Props) {
  return (
    <>
      <h2 className="apply-step-title">The heart of it</h2>
      <p className="apply-step-body">
        These three questions matter more to us than résumé bullets. There are no wrong answers — write the way you talk.
      </p>
      <div className="apply-form-group">
        <label htmlFor="bringLife">What does it mean to you to bring life back into someone's home?</label>
        <textarea id="bringLife" rows={5} value={p.bringLife} onChange={(e) => p.setBringLife(e.target.value)} />
      </div>
      <div className="apply-form-group">
        <label htmlFor="costlyService">
          Tell us about a time you served someone when it cost you something. What did it cost — and would you do it again?
        </label>
        <textarea id="costlyService" rows={6} value={p.costlyService} onChange={(e) => p.setCostlyService(e.target.value)} />
      </div>
      <div className="apply-form-group">
        <label htmlFor="yearFromNow">
          A year from now, what would a good year here have done for you — in your work, and in your life?
        </label>
        <textarea id="yearFromNow" rows={5} value={p.yearFromNow} onChange={(e) => p.setYearFromNow(e.target.value)} />
      </div>
    </>
  );
}

interface Step6Props {
  signed: boolean;
  setSigned: (v: boolean) => void;
}

function Step6Appearance(p: Step6Props) {
  const branding = useBranding();
  return (
    <>
      <h2 className="apply-step-title">Photo &amp; video release</h2>
      <p className="apply-step-body">
        Optional. Sometimes we share our team's faces on our website and social media so clients can put a name to the person walking into their home. If you'd rather we didn't, no problem — just leave this unchecked.
      </p>
      <div className="apply-release-block">
        <p>
          {/*
            Like the TCPA line on /book-today, this is a consent naming a legal
            entity — it was hard-coded to New Leaf Cleaning, LLC for every
            tenant. The entity suffix is kept only for New Leaf, because we do
            not know another company's registered form.
          */}
          By checking the box below, I consent to {branding.isNewLeaf ? NEW_LEAF_APPLY.legalEntity : branding.name} using my name, voice, photographs, and likeness in advertising, promotion, and distribution materials including video, audio, print, the website, and social media. I understand this consent is voluntary and may be revoked in writing at any time.
        </p>
        <p className="apply-muted">
          This is completely optional. We will never share photos that compromise client privacy.
        </p>
      </div>
      <label className="apply-checkbox-row apply-checkbox-row-prominent">
        <input type="checkbox" checked={p.signed} onChange={(e) => p.setSigned(e.target.checked)} />
        <span>I consent to the photo &amp; video release as described above.</span>
      </label>
    </>
  );
}

interface Step7Props {
  fullName: string;
  email: string;
  phone: string;
  mailingAddress: string;
  experienceTypes: ApplicantExperienceType[];
  yearsExperience: number | '';
}

function Step7Review(p: Step7Props) {
  return (
    <>
      <h2 className="apply-step-title">Before you sign</h2>
      <p className="apply-step-body">A quick summary so you know what we have:</p>
      <dl className="apply-review-list">
        <dt>Name</dt><dd>{p.fullName || '—'}</dd>
        <dt>Email</dt><dd>{p.email || '—'}</dd>
        <dt>Phone</dt><dd>{p.phone || '—'}</dd>
        <dt>Mailing address</dt><dd>{p.mailingAddress || '—'}</dd>
        <dt>Experience</dt>
        <dd>
          {p.experienceTypes.length === 0 ? 'New to this' : p.experienceTypes.map((t) => EXPERIENCE_LABELS[t]).join(', ')}
          {typeof p.yearsExperience === 'number' && p.yearsExperience > 0 && <> · {p.yearsExperience} {p.yearsExperience === 1 ? 'year' : 'years'}</>}
        </dd>
      </dl>
      <p className="apply-step-body">
        On the next step you'll sign by typing your full name. After that, you'll hear from us within 3 business days.
      </p>
    </>
  );
}

interface Step8Props {
  applicantSignature: string; setApplicantSignature: (v: string) => void;
  confirmAccurate: boolean; setConfirmAccurate: (v: boolean) => void;
}

function Step8Sign(p: Step8Props) {
  return (
    <>
      <h2 className="apply-step-title">Sign &amp; submit</h2>
      <p className="apply-step-body">
        By signing, you confirm that what you've shared is true to the best of your knowledge. We'll keep it between us and the team that needs to read it.
      </p>
      <div className="apply-form-group">
        <label htmlFor="signature">Type your full name to sign</label>
        <input
          id="signature"
          type="text"
          value={p.applicantSignature}
          onChange={(e) => p.setApplicantSignature(e.target.value)}
          autoComplete="name"
          className="apply-signature-input"
        />
      </div>
      <label className="apply-checkbox-row apply-checkbox-row-prominent">
        <input
          type="checkbox"
          checked={p.confirmAccurate}
          onChange={(e) => p.setConfirmAccurate(e.target.checked)}
        />
        <span>I confirm that the information I've provided is accurate and complete to the best of my knowledge.</span>
      </label>
    </>
  );
}

// ===================================================================
// Nav buttons
// ===================================================================
interface NavProps {
  step: Step;
  canBack: boolean;
  isSubmitting: boolean;
  onBack: () => void;
  onNext: () => void;
  onSubmit: () => void;
}

function ApplyNavButtons({ step, canBack, isSubmitting, onBack, onNext, onSubmit }: NavProps) {
  const isFinal = step === 8;
  return (
    <div className="apply-nav">
      {canBack && (
        <button type="button" className="apply-btn apply-btn-secondary" onClick={onBack} disabled={isSubmitting}>
          Back
        </button>
      )}
      {!isFinal && (
        <button type="button" className="apply-btn apply-btn-primary" onClick={onNext}>
          Next
        </button>
      )}
      {isFinal && (
        <button type="button" className="apply-btn apply-btn-primary" onClick={onSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Submitting...' : 'Submit application'}
        </button>
      )}
    </div>
  );
}

// ===================================================================
// End-state screens
// ===================================================================

function SubmittedScreen({ fullName }: { fullName: string }) {
  const branding = useBranding();
  const firstName = fullName.split(/\s+/)[0] || 'there';
  return (
    <div className="apply-page">
      <Manifesto />
      <div className="apply-shell">
        <div className="apply-card apply-card-success">
          <div className="apply-success-mark" aria-hidden="true">✓</div>
          <h2 className="apply-step-title">We got it, {firstName}.</h2>
          <p className="apply-step-body">
            Your application landed safely with us. We read every one personally.{branding.isNewLeaf ? NEW_LEAF_APPLY.founderClause : ''}
          </p>
          <p className="apply-step-body">
            <strong>You'll hear from us within 3 business days.</strong> Check your email for a confirmation in the next few minutes.
          </p>
          <p className="apply-step-body apply-muted">
            If anything in your life changes between now and then, just reply to that email.
          </p>
          <p className="apply-step-body" style={{ marginTop: 32 }}>
            <Link to="/" className="apply-link">← Back to {branding.name}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function ExpiredScreen() {
  return (
    <div className="apply-page">
      <Manifesto />
      <div className="apply-shell">
        <div className="apply-card">
          <h2 className="apply-step-title">This link expired</h2>
          <p className="apply-step-body">
            Application links work for 30 days. No worries — you can start a fresh one.
          </p>
          <Link to="/apply" className="apply-btn apply-btn-primary apply-btn-link">Start a new application</Link>
        </div>
      </div>
    </div>
  );
}

// ===================================================================
// Helpers
// ===================================================================
function isValidEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}
